/**
 * OFAC sanctioned-destination guard — single SoT for both the type-level
 * `ShippableCountry` ban and the runtime check used at every label-buying
 * boundary.
 *
 * Source: US Treasury OFAC SDN / country sanctions program list as of
 * 2026-06-24. **Baseline only** — the formal list is broader and changes
 * by program (CAATSA, NDAA, EO-13884, etc.). The 6 codes pinned here are
 * the comprehensive country-level embargo set; sectoral / SDN-list-only
 * sanctions are NOT modelled (that needs per-recipient screening, not a
 * country gate).
 *
 * Reference:
 *   https://ofac.treasury.gov/sanctions-programs-and-country-information
 *
 * MAINTENANCE:
 *   - Re-verify quarterly against the OFAC source page above.
 *   - Any operator-noticed regulatory update → bump the date in this header,
 *     add/remove ISO-3166 alpha-2 codes below, re-run integration tests.
 *   - This is a **regulatory blocker**, not a coverage choice — a label to
 *     a sanctioned destination is a potential OFAC violation. The guard
 *     MUST fire at the rates boundary BEFORE Shippo is asked to quote, and
 *     again at every downstream purchase path as defence-in-depth.
 *
 * Codes are ISO-3166 alpha-2 uppercase.
 *
 * @see .tondocs/shippo/PLAN.md §O.2.7 — type-level + runtime guard scope
 * @see .tondocs/shippo/PLAN.md §K.10 axis 9 — narrow type approach
 * @see .tondocs/shippo/ORIGIN_COUNTRY_SUPPORT.md §2.1 — US-origin → worldwide
 *      means this gate can fire today without it
 */

/**
 * OFAC country-level embargo baseline. Order is alphabetical for diff
 * stability; insertion order does NOT carry meaning.
 *
 * Current baseline (2026-06-24):
 *   - BY — Belarus
 *   - CU — Cuba
 *   - IR — Iran
 *   - KP — North Korea (DPRK)
 *   - RU — Russia
 *   - SY — Syria
 */
export const SANCTIONED_COUNTRIES = ["BY", "CU", "IR", "KP", "RU", "SY"] as const;

/**
 * Discriminator literal type — the type-level half of the §O.2.7 guard.
 * Use with `Exclude<>` to build a `ShippableCountry` brand:
 *
 * ```ts
 * type CountryCode = string; // ISO-3166 alpha-2, broad
 * type ShippableCountry = Exclude<CountryCode, SanctionedCountry>;
 * ```
 *
 * The narrow approach (PLAN §K.10 axis 9): `CountryCode` stays open
 * `string` to accept any ISO-2 from the wire; `ShippableCountry` is the
 * post-guard brand returned only by `assertShippableCountry`. Code paths
 * that buy labels accept `ShippableCountry`, not `CountryCode` — making
 * it a type error to pipe an un-screened country into the purchase boundary.
 */
export type SanctionedCountry = (typeof SANCTIONED_COUNTRIES)[number];

/**
 * Branded "this country passed the OFAC gate" type. The brand has no
 * runtime cost; it exists only so the compiler can refuse a raw `string`
 * being passed to a label-purchase boundary.
 *
 * The only way to obtain a `ShippableCountry` is via `assertShippableCountry`
 * (or its predicate sibling `isSanctionedCountry`) which performs the
 * runtime check.
 */
export type ShippableCountry = string & { readonly __brand: "ShippableCountry" };

/**
 * Runtime predicate. Normalises to uppercase before comparison so a wire
 * `"cu"` is treated the same as `"CU"` (defence against case-naive callers
 * — the rates request schema accepts `z.string().min(2)`, NOT a literal
 * uppercase union, so we cannot assume the caller has already uppercased).
 */
export function isSanctionedCountry(
  country: string,
): country is SanctionedCountry {
  const normalised = country.trim().toUpperCase();
  return (SANCTIONED_COUNTRIES as readonly string[]).includes(normalised);
}

/**
 * Assertion form — throws `SanctionedDestinationError` if the country is
 * embargoed. Use at low-level boundaries (Shippo SDK wrappers) where the
 * call already commits to network IO and a thrown error is the cleanest
 * signal. Route handlers should prefer the predicate form + explicit
 * 422 reply.
 */
export class SanctionedDestinationError extends Error {
  readonly code = "SHIPPING_SANCTIONED_DESTINATION" as const;
  readonly country: string;
  constructor(country: string) {
    super(`Shipping to sanctioned destination refused: ${country}`);
    this.name = "SanctionedDestinationError";
    this.country = country.trim().toUpperCase();
  }
}

export function assertShippableCountry(country: string): ShippableCountry {
  if (isSanctionedCountry(country)) {
    throw new SanctionedDestinationError(country);
  }
  return country.trim().toUpperCase() as ShippableCountry;
}
